import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentService } from 'src/app/views/content/content.service';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MaterialModule } from '../../material.module';

export interface CoursePlanLecture {
  title: string;
  subject?: string;
}

export interface SectionPlan {
  title: string;
  lectures: CoursePlanLecture[];
}

export interface SectionDialogData {
  mode: 'create' | 'edit';
  courseID: string;
  courseName?: string;
  sectionID?: string;
  sectionName?: string;
}

@Component({
  selector: 'app-section-dialog',
  templateUrl: './section-dialog.component.html',
  styleUrls: ['./section-dialog.component.scss'],
  standalone: true,
  imports: [MaterialModule, TranslateModule, FormsModule, DragDropModule, MatProgressSpinnerModule],
})
export class SectionDialogComponent implements OnInit {
  loading = false;
  buttonLock = false;
  sectionName = '';
  selectedLanguage = this.translate.currentLang || 'fr';
  course: any;
  section: any;

  isGenerating = false;
  showTOC = false;
  instructions = '';
  
  plan: SectionPlan = { title: '', lectures: [] };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SectionDialogData,
    public dialogRef: MatDialogRef<SectionDialogComponent>,
    private toast: ToastService,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService,
    private contentService: ContentService
  ) {}

  ngOnInit() {
    if (this.data.mode === 'edit') {
      this.initEditMode();
    } else {
      this.sectionName = this.data.sectionName || '';
    }
  }

  initEditMode() {
    this.loading = true;
    this.contentService.getTheCourse(this.data.courseID).subscribe({
      next: (course) => {
        this.loading = false;
        this.course = course;
        
        if (this.data.sectionID && this.course.sections) {
          const foundSection = this.course.sections.find((s: any) => s._id === this.data.sectionID);
          if (foundSection) {
            this.section = foundSection;
            let actualName = foundSection.name;
            if (foundSection.__name) {
              const edition = foundSection.__name.editions.find((e: any) => e.language === this.selectedLanguage);
              if (edition) {
                actualName = edition.content;
              }
            }
            this.sectionName = actualName;
          }
        }
      },
      error: (err) => {
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.CreatingCourseFail'), 'Ok', { duration: 2000 });
      },
    });
  }

  onSubmit() {
    if (this.showTOC) {
       this.sectionName = this.plan.title;
    }
    if (!this.sectionName.trim()) return;

    if (this.data.mode === 'create') {
      this.createSection();
    } else if (this.data.mode === 'edit') {
      this.editSection();
    }
  }

  createSection() {
    this.buttonLock = true;
    const payload: any = {
      _id: this.data.courseID,
      sectionName: this.sectionName,
      language: this.selectedLanguage,
    };
    
    if (this.showTOC) {
      payload.plan = this.plan;
    }

    this.contentService
      .courseAddSection(payload)
      .subscribe({
        next: (res) => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
          this.dialogRef.close('ok');
        },
        error: (err) => {
          this.buttonLock = false;
          if (!this.errorHandler.handle401Error(err)) {
            this.toast.snackbar.open(this.translate.instant('Snackbar.CreatingSectionFail'), 'Ok', { duration: 2000 });
          }
        },
      });
  }

  editSection() {
    if (!this.section) {
       this.dialogRef.close();
       return;
    }
    
    this.buttonLock = true;
    this.section.name = this.sectionName;
    this.contentService
      .editSection({
        courseID: this.course._id,
        section: this.section,
        language: this.selectedLanguage,
        name: this.sectionName,
      })
      .subscribe({
        next: (res) => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
          this.dialogRef.close('ok');
        },
        error: (err) => {
          this.buttonLock = false;
          if (!this.errorHandler.handle401Error(err)) {
            this.toast.snackbar.open(this.translate.instant('Snackbar.EditingSectionFail'), 'Ok', { duration: 2000 });
          }
        }
      });
  }

  generateSectionTOC() {
    this.isGenerating = true;
    this.contentService.generateSectionPlan({
        courseName: this.data.courseName || 'Course',
        sectionTitle: this.sectionName,
        language: this.selectedLanguage,
        instructions: this.instructions
    }).subscribe({
       next: (plan) => {
         this.plan = plan;
         this.sectionName = plan.title;
         this.showTOC = true;
         this.isGenerating = false;
       },
       error: (err) => {
         console.error(err);
         this.isGenerating = false;
         this.toast.snackbar.open(this.translate.instant('Snackbar.EmbeddingGenerationFailed'), 'Ok', {duration: 2000});
       }
    });
  }

  regenerateSectionTOC() {
    this.generateSectionTOC();
  }

  dropLecture(event: CdkDragDrop<CoursePlanLecture[]>) {
    moveItemInArray(this.plan.lectures, event.previousIndex, event.currentIndex);
  }
  
  addLecture() {
    this.plan.lectures.push({ title: 'New Lecture', subject: '' });
  }
  
  deleteLecture(index: number) {
     this.plan.lectures.splice(index, 1);
  }
}
